import React, { Context, createContext, PropsWithChildren, useCallback, useContext, useRef, useState } from 'react'
import { connect, createLocalTracks, Room } from 'twilio-video'
import { callApi, TApiResponse } from '../api'
import { useAuthContext } from '../auth'
import { useHistory } from 'react-router-dom'
import { ECallLogTypes, ICall, TCall, TCallActions, TCallData, TJoinCallOptions } from '../types'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialCallState: ICall = {
	loading: false,
	audioMuted: false,
	videoMuted: false,
}

const CallContext: Context<{
	call: ICall
	actions: TCallActions
}> = createContext({
	call: initialCallState,
	actions: {
		joinCall: (id: string, options: TJoinCallOptions) => {},
		joinExpertCall: (id: string, options: TJoinCallOptions) => {},
		setError: (id: string) => {},
		disconnect: () => {},
		audioMuteToggle: () => {},
		videoMuteToggle: () => {},
		getCallById: (id: string) => {},
		clearCallData: () => {},
		setCallMessage: (message: string) => {},
	},
})

export const CallProvider = (props: PropsWithChildren<{}>) => {
	const [call, setCall] = useState<TCall>()
	const [callData, setCallData] = useState<TCallData>()
	const [callMessage, setCallMessage] = useState<string>('')
	const [callToken, setCallToken] = useState<string>()
	const [participant, setParticipant] = useState<ICall['participant']>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setProviderError] = useState<string | null>(null)
	const [room, setRoom] = useState<Room>()
	const [audioMuted, setAudioMuted] = useState<boolean>(false)
	const [videoMuted, setVideoMuted] = useState<boolean>(false)

	const { auth } = useAuthContext()
	const history = useHistory()
	const timeIdRef = useRef(null)
	const log = useCallback(
		(id: string, type: ECallLogTypes, message?: string) => {
			callApi(`/call/log/${id}`, {
				method: 'post',
				body: { first_name: auth.user?.first_name, last_name: auth.user?.last_name, type, message },
			})
		},
		[auth?.user?.id]
	)

	const fetchCall = async (id: string, options?: TJoinCallOptions) => {
		const { first_name, last_name, email, media } = options
		setLoading(true)
		setParticipant({
			firstName: auth.user?.first_name,
			lastName: auth.user?.last_name,
			email: auth.user?.email,
			phone: auth.user?.phone,
			phoneCountryCode: auth.user?.phone_country_code,
			phoneCountryId: auth.user?.phone_country_id,
		})
		const callResponse: TApiResponse = await callApi(`/call/${id}`, {
			method: 'post',
			body: { first_name, last_name, email },
		})

		const { data } = callResponse.payload

		clearTimeout(timeIdRef?.current)
		if (callResponse.ok) {
			setProviderError(null)
			setCall(data.call)
			if (data.twilioToken) {
				setCallMessage('')
				try {
					const tracks = await createLocalTracks(media)
					const newRoom = await connect(data.twilioToken, {
						name: data.call.twilio,
						dominantSpeaker: true,
						region: 'us1',
						tracks,
					})
					setCallToken(data.twilioToken)
					setRoom(newRoom)
				} catch (err) {
					log(id, ECallLogTypes.CONNECT_ERROR, err?.message)
					setProviderError(err?.message)
				}
				setLoading(false)
			} else {
				if (data.message) {
					setCallMessage(data.message)
				}
				timeIdRef.current = setTimeout(() => fetchCall(id, options), 5000)
			}
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Call!')
		}
	}

	const clearCallData = () => {
		setCallData(null)
	}

	const getCallById = async (id: string) => {
		setLoading(true)
		const callResponse: TApiResponse = await callApi(`/call/${id}`, {
			method: 'get',
		})
		const { data } = callResponse.payload

		clearTimeout(timeIdRef?.current)

		if (data.call.cancelled_on) {
			setLoading(false)
			return setProviderError('This call has been canceled')
		}

		if (callResponse.ok) {
			setProviderError(null)
			setCallData(data.call)
			setLoading(false)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Call!')
		}
	}

	const fetchExpertCall = async (id: string, options?: TJoinCallOptions) => {
		const { media } = options
		setLoading(true)
		setParticipant({
			firstName: auth.user.first_name,
			lastName: auth.user.last_name,
			email: auth.user.email,
			phone: auth.user.phone,
			phoneCountryCode: auth.user.phone_country_code,
			phoneCountryId: auth.user.phone_country_id,
		})
		const callResponse: TApiResponse = await callApi(`/call/expert/${id}`, {})

		const { data } = callResponse.payload
		if (callResponse.ok) {
			setProviderError(null)
			try {
				const tracks = await createLocalTracks(media)
				const newRoom = await connect(data.twilioToken, {
					name: data.call.twilio,
					dominantSpeaker: true,
					region: 'us1',
					tracks,
				})
				setCallToken(data.twilioToken)
				setRoom(newRoom)
				setCall(data.call)
			} catch (err) {
				log(id, ECallLogTypes.CONNECT_ERROR, err?.message)
				setProviderError(err?.message)
			}
			setLoading(false)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Call!')
		}
	}

	const fetchDisconnect = async () => {
		setCall(null)
		setCallToken(null)
		setRoom(null)
		clearTimeout(timeIdRef?.current)
		setProviderError(null)
		setLoading(false)
		history.push(auth.user ? '/profile/calls' : '/login')
	}

	const joinCall = useCallback(fetchCall, [fetchCall])
	const joinExpertCall = useCallback(fetchExpertCall, [auth?.user?.first_name, auth?.user?.last_name, log])
	const disconnect = useCallback(fetchDisconnect, [call, auth.user?.id])
	const audioMuteToggle = useCallback(() => setAudioMuted(!audioMuted), [audioMuted])
	const videoMuteToggle = useCallback(() => setVideoMuted(!videoMuted), [videoMuted])

	const actions: TCallActions = {
		joinCall,
		joinExpertCall,
		disconnect,
		audioMuteToggle,
		videoMuteToggle,
		setError: setProviderError,
		getCallById,
		clearCallData,
		setCallMessage,
	}

	return (
		<CallContext.Provider
			value={{
				call: { audioMuted, videoMuted, call, callToken, loading, error, room, callData, callMessage, participant },
				actions,
			}}
		>
			{props.children}
		</CallContext.Provider>
	)
}

export const useCallContext: () => { call: ICall; actions: TCallActions } = () => {
	return useContext(CallContext)
}
